import {
  NEW_THREAD_THREAD_ID,
  OpenThreadTabType,
  ThreadIdType,
} from "@/state/@types";

export const DEFAULT_OPEN_THREAD_TABS: OpenThreadTabType[] = [
  { id: NEW_THREAD_THREAD_ID, label: "New thread" },
];

/** Map ``activeThreadNodeId`` (null = draft) to a tab id. */
export function threadNodeIdToTabId(nodeId: string | null): ThreadIdType {
  return nodeId ?? NEW_THREAD_THREAD_ID;
}

/** Map a tab id back to ``activeThreadNodeId``. */
export function tabIdToThreadNodeId(tabId: ThreadIdType): string | null {
  return tabId === NEW_THREAD_THREAD_ID ? null : tabId;
}

export function defaultTabLabel(tabId: ThreadIdType): string {
  return tabId === NEW_THREAD_THREAD_ID ? "New thread" : "Thread";
}

export function ensureOpenThreadTabs(
  tabs: OpenThreadTabType[] | undefined,
): OpenThreadTabType[] {
  if (!tabs?.length) return [...DEFAULT_OPEN_THREAD_TABS];
  return tabs;
}

/** After persist rehydrate, ensure the active thread appears in the tab strip. */
/** Reorder open tabs; unknown ids are appended; returns input if length would change. */
export function reorderThreadTabs(
  tabs: OpenThreadTabType[],
  orderedTabIds: ThreadIdType[],
): OpenThreadTabType[] {
  const byId = new Map(tabs.map((t) => [t.id, t]));
  const next: OpenThreadTabType[] = [];
  for (const id of orderedTabIds) {
    const tab = byId.get(id);
    if (tab) {
      next.push(tab);
      byId.delete(id);
    }
  }
  for (const tab of byId.values()) {
    next.push(tab);
  }
  if (next.length !== tabs.length) return tabs;
  const unchanged = next.every((tab, index) => tab.id === tabs[index]?.id);
  return unchanged ? tabs : next;
}

export function reconcileOpenThreadTabs(
  tabs: OpenThreadTabType[],
  activeThreadNodeId: string | null,
): OpenThreadTabType[] {
  const next = ensureOpenThreadTabs(tabs);
  const activeTabId = threadNodeIdToTabId(activeThreadNodeId);
  if (next.some((t) => t.id === activeTabId)) return next;
  if (activeTabId === NEW_THREAD_THREAD_ID) return next;
  return [...next, { id: activeTabId }];
}
